import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PortalVersionContext = createContext(undefined);

// Phase 2 storage key — bumped from 'palcare-portal-version' so existing
// users (who all had 'v1' auto-persisted under phase 1) start fresh on v2.
const STORAGE_KEY = 'palcare-portal-version-p2';

/**
 * Determines the initial portal version:
 * 1. Check localStorage for a persisted preference
 * 2. Default to 'v2' (phase 2 — new portal is the default experience)
 */
function getInitialVersion() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'v1' || stored === 'v2') return stored;
    } catch {
        // localStorage may be unavailable
    }
    return 'v2';
}

export function PortalVersionProvider({ children }) {
    const [portalVersion, setVersionState] = useState(getInitialVersion);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, portalVersion);
        } catch {
            // silently ignore storage errors
        }
    }, [portalVersion]);

    const togglePortalVersion = useCallback(() => {
        setVersionState((prev) => (prev === 'v1' ? 'v2' : 'v1'));
    }, []);

    const setPortalVersion = useCallback((newVersion) => {
        if (newVersion === 'v1' || newVersion === 'v2') {
            setVersionState(newVersion);
        }
    }, []);

    return (
        <PortalVersionContext.Provider value={{ portalVersion, togglePortalVersion, setPortalVersion }}>
            {children}
        </PortalVersionContext.Provider>
    );
}

export function usePortalVersion() {
    const context = useContext(PortalVersionContext);
    if (context === undefined) {
        throw new Error('usePortalVersion must be used within a PortalVersionProvider');
    }
    return context;
}

export default PortalVersionContext;
